import {DataModificationsHeader} from 'audit/DataModifications';
import {Concentration, LegacyMeasurement} from 'constants/MeasurementConstants';
import {
  Batch,
  ContainerContent,
  ContainerTransfer,
  TransferContainerSummary,
} from 'constants/SampleConstants';
import {SampleControlParams} from 'constants/SampleStatusConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import {BioentitySummary} from 'folder-items/FileConstants';
import {CreateGridParams} from 'samples/GridDAO';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

export interface ContainerInvalidatableResponse {
  invalidatableContainedItemIds: Array<string>;
  invalidatableStorableIds: Array<string>;
  // Deprecated - use invalidatableContainedItemIds instead
  invalidatableBatchIds: Array<string>;
}

export interface ExistingGridParams {
  outputGridId: string;
}

// Keep in sync with backend CONTAINER_TRANSFER_DICTS_API_SPEC
export interface ContainerTransferParam {
  outputContainer: {id: string};
  quantity: LegacyMeasurement;
  inputSampleId: string | null; // null for dilutions
}

// Keep in sync with backend CONTAINER_CONTENT_DICTS_API_SPEC
export interface ContainerContentParam {
  containedItemId: string;
  containerId: string;
  concentration: Concentration;
}

export function transferGrids(
  containerContents: Array<ContainerContentParam>,
  inputGridId: string,
  shouldTransferAllQuantities: boolean,
  transferQuantity: LegacyMeasurement,
  folderId?: string,
  createGridArgs?: CreateGridParams,
  existingGridArgs?: ExistingGridParams,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<ContainerInvalidatableResponse> {
  return TaskRunner.postJSON({
    data: {
      inputGridId,
      containerContents,
      shouldTransferAllQuantities,
      transferQuantity,
      createGridArgs,
      existingGridArgs,
    },
    url: '/1/api/transfers/actions/transfer-plate-to-plate',
    extraHeaders: dataModsHeader,
  }).done(() => {
    // folderId is present as an arg only when a new grid is created.
    // If present, invalidate that folder in the cache so that the sidepanel will update with the new grid.
    if (folderId) {
      FolderDAOCache.invalidateFolder(folderId);
    }
  });
}

export type TransferContainersResponse = ContainerInvalidatableResponse & {undoToken: string};

export function transferContainers(
  containerTransfers: Array<ContainerTransferParam>,
  containerContents: Array<ContainerContentParam>,
  outputSampleControls: SampleControlParams,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<TransferContainersResponse> {
  return net.postJSON({
    url: '/1/api/transfers/actions/transfer-containers',
    data: {containerTransfers, containerContents, ...outputSampleControls},
    extraHeaders: dataModsHeader,
  });
}

export function undoTransfers(undoToken: string): JQueryPromise<{}> {
  return net.postJSON({
    url: '/1/api/transfers:undo',
    data: {undoToken},
  });
}

export interface CalculateConcentrationsResponse {
  containerTransfers: Array<ContainerTransfer & {outputContainer: TransferContainerSummary}>;
  batches: Array<Batch>;
  containedBioentities: Array<BioentitySummary>;
  containerContents: Array<ContainerContent & {container: TransferContainerSummary}>;
}

export function calculateConcentrations(
  containerTransfers: Array<ContainerTransferParam>,
  shouldCalculateConcentrations: boolean,
  inputContainableConcentration: LegacyMeasurement | null
): Promise<CalculateConcentrationsResponse> {
  return net.postJSONPromise({
    url: '/1/api/transfers:calculate-concentrations',
    data: {containerTransfers, shouldCalculateConcentrations, inputContainableConcentration},
  });
}

export function calculateConcentrationsGridToGrid(
  inputGridId: string,
  outputGridId: string,
  shouldTransferAllQuantities: boolean,
  transferQuantity: LegacyMeasurement
): JQueryPromise<CalculateConcentrationsResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/transfers/actions/tasks:calculate-concentrations-plate-to-plate',
    data: {
      inputGridId,
      outputGridId,
      shouldTransferAllQuantities,
      transferQuantity,
    },
  });
}
